import { BackNextButton } from 'app/components/BackNextButton';
import React from 'react';
import styled from 'styled-components';
import { TextField } from 'app/components/TextField';
import {
  ApiError,
  GeneralLookup,
  LookupService,
  RegisterRider,
} from 'services/openapi';
import useApi from 'services/mavapi/useApi';
import { Button } from '@progress/kendo-react-buttons';
import AdminCustomButton from 'app/components/AdminCustomButton';

type Props = {
  onClickPlacementTwoBack?: () => void;
  handlePlacementSubmit?: (dataToSubmit?: RegisterRider) => void;
  riderData: RegisterRider;
  setRiderData?: React.Dispatch<React.SetStateAction<RegisterRider>>;
};

const IndividualPlacementFormTwo = ({
  onClickPlacementTwoBack,
  handlePlacementSubmit,
  riderData,
  setRiderData,
}: Props) => {
  const [associationLookUp, setAssociationLookUp] =
    React.useState<GeneralLookup[]>();
  const { error: apiError, handleRequest } = useApi();
  const [, setError] = React.useState<ApiError | unknown>();
  const { lookupGetAllLookups } = LookupService;
  const [isButtonDisabled, setIsButtonDisabled] = React.useState(true);
  const [isInitialized, setIsInitialized] = React.useState(false);

  function getAllLookupData(names?: string) {
    handleRequest(lookupGetAllLookups(names))
      .then(data => {
        if (data) {
          const associationValues = data?.[0]?.values || [];
          setAssociationLookUp(associationValues);

          setRiderData?.(prevData => {
            // Check if there's existing data with actual values
            const existingAssociations = prevData.riderAssociations || [];
            const hasExistingData = existingAssociations.some(
              assoc => assoc?.name?.trim() !== '' || assoc?.idNumber?.trim() !== ''
            );

            // Create a map of existing associations by their association ID for easy lookup
            const existingDataMap = new Map();
            existingAssociations.forEach(assoc => {
              const assocId = assoc?.association?.id;
              if (assocId && (assoc?.name?.trim() !== '' || assoc?.idNumber?.trim() !== '')) {
                existingDataMap.set(assocId, assoc);
              }
            });

            // Map lookup associations to riderAssociations, preserving any existing data
            const initializedAssociations = associationValues.map((lookupAssoc) => {
              const existingData = existingDataMap.get(lookupAssoc?.id);

              if (existingData) {
                // Preserve existing data and update association details
                return {
                  name: existingData.name ?? '',
                  idNumber: existingData.idNumber ?? '',
                  association: {
                    id: lookupAssoc?.id ?? '',
                    name: lookupAssoc?.name ?? '',
                    code: lookupAssoc?.code,
                    children: lookupAssoc?.children,
                    otherName: lookupAssoc?.otherName,
                    isAdminLive: lookupAssoc?.isAdminLive,
                  },
                };
              } else {
                // Create new empty entry
                return {
                  association: {
                    id: lookupAssoc?.id ?? '',
                    name: lookupAssoc?.name ?? '',
                    code: lookupAssoc?.code,
                    children: lookupAssoc?.children,
                    otherName: lookupAssoc?.otherName,
                    isAdminLive: lookupAssoc?.isAdminLive,
                  },
                  name: '',
                  idNumber: '',
                };
              }
            });

            return {
              ...prevData,
              riderAssociations: initializedAssociations,
            };
          });

          setIsInitialized(true);
        }
      })
      .catch(() => setError(apiError));
  }

  React.useEffect(() => {
    if (!isInitialized) {
      getAllLookupData('ASSOCIATIONS');
    }
  }, []);

  const handleFieldChange = (index, field, value) => {
    setRiderData?.(prevData => {
      const updatedRiderAssociations = [...prevData.riderAssociations!];

      // Ensure the array is long enough
      while (updatedRiderAssociations.length <= index) {
        updatedRiderAssociations.push({
          association: { id: '', name: '', code: '', children: null, otherName: '', isAdminLive: false },
          name: '',
          idNumber: '',
        });
      }

      // Update the specific field at the given index
      updatedRiderAssociations[index] = {
        ...updatedRiderAssociations[index],
        [field]: value,
      };

      return { ...prevData, riderAssociations: updatedRiderAssociations };
    });
  };

  // Check if all filled entries are valid (have both name and idNumber)
  React.useEffect(() => {
    const areAllMappingsValid = riderData?.riderAssociations?.every(
      value =>
        (value?.name?.trim() && value?.idNumber?.trim()) ||
        (!value?.name?.trim() && !value?.idNumber?.trim()),
    );
    setIsButtonDisabled(!areAllMappingsValid);
  }, [riderData?.riderAssociations]);

  // Handle Skip button click - clear all associations and submit
  const handleSkipClick = () => {
    // Create updated rider data with empty associations
    const updatedRiderDataWithEmptyAssociations = {
      ...riderData,
      riderAssociations: [],
    };
    setRiderData?.(updatedRiderDataWithEmptyAssociations);
    // Pass the updated data directly to parent's submit handler
    handlePlacementSubmit?.(updatedRiderDataWithEmptyAssociations);
  };

  // Handle Pay button click - filter out empty entries
  const handlePayClick = () => {
    const filteredAssociations = riderData?.riderAssociations?.filter(
      obj => obj.name?.trim() !== '' && obj.idNumber?.trim() !== '',
    ) || [];

    // Create updated rider data with filtered associations
    const updatedRiderDataWithFilteredAssociations = {
      ...riderData,
      riderAssociations: filteredAssociations,
    };
    setRiderData?.(updatedRiderDataWithFilteredAssociations);
    // Pass the updated data directly to parent's submit handler
    handlePlacementSubmit?.(updatedRiderDataWithFilteredAssociations);
  };

  return (
    <MainWrapper className="form-MainWrapper">
      <OverflowWrapper className="OverflowWrapper">
        <p className="heading-para">TO DOCUMENT PRIOR COMPETITION RECORDS:</p>
        <p className="para-two">
          <span>
            LIST YOUR NAME EXACTLY AS IT APPEARS IN ASSOCIATION RECORDS, AND{' '}
          </span>
          list your ID# for each association in which you have competed. (Not
          one in which you are simply a card holding member who has never
          participated in competition).
        </p>
        <MappedDataWrapper>
          {associationLookUp?.map((value, index) => (
            <TextFieldGrid key={`${value.id}-${index}`}>
              <p className="para-three">{value?.name}</p>
              <StyledNameField
                value={riderData?.riderAssociations?.[index]?.name || ''}
                onChange={e => handleFieldChange(index, 'name', e.target.value)}
                name={`name${index + 1}`}
                placeholder="name"
              />

              <StyledIdField
                value={riderData?.riderAssociations?.[index]?.idNumber || ''}
                onChange={e =>
                  handleFieldChange(index, 'idNumber', e.target.value)
                }
                name={`id${index + 1}`}
                placeholder="ID number"
              />
            </TextFieldGrid>
          ))}
        </MappedDataWrapper>
        <ButtonWrapper className="bg-color-change skip-section">
          <AdminCustomButton
            buttonType="cancel"
            onClick={handleSkipClick}
          >
            Skip
          </AdminCustomButton>
          <BackNextButton
            classNameBack="btn-back"
            classNameNxt="btn-nxt"
            onClickNxt={handlePayClick}
            onClickBack={onClickPlacementTwoBack}
            next="Pay"
            back="Back"
            disableNext={isButtonDisabled}
          />
        </ButtonWrapper>
      </OverflowWrapper>
    </MainWrapper>
  );
};

const MainWrapper = styled.div`
  height: calc(100vh - 220px);
  width: 100%;
  position: relative;
  @media (min-height: 100px) and (max-height: 696px) {
    height: calc(100vh - 220px);
  }
  .heading-para {
    color: #ff9900;
    font-size: 13px;
  }
  .para-two {
    color: #a7a7a7;
    font-size: 13px;
  }
  span {
    text-transform: uppercase;
  }
  @media (min-width: 576px) and (max-width: 767px) {
    .heading-para {
      font-size: 11px;
    }
    .para-two {
      font-size: 11px;
    }
  }
  @media (min-width: 768px) and (max-width: 991px) {
    .heading-para {
      font-size: 12px;
    }
    .para-two {
      font-size: 12px;
    }
  }
  @media (min-width: 992px) and (max-width: 1199px) {
    .heading-para {
      font-size: 13px;
    }
    .para-two {
      font-size: 13px;
    }
  }
`;
const OverflowWrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow: auto;
  padding-bottom: 100px;
  ::-webkit-scrollbar {
    width: 5px;
    margin-left: 3px;
  }
  ::-webkit-scrollbar-track {
    background: transparent !important;
    border-radius: 10px;
  }
  ::-webkit-scrollbar-thumb {
    width: 5px;
    background: transparent !important;
    border-radius: 10px;
  }
`;
const MappedDataWrapper = styled.div`
  height: calc(100vh - 225px);
  overflow: auto;
  padding-bottom: 100px;
  ::-webkit-scrollbar {
    width: 5px;
    margin-left: 3px;
  }
  ::-webkit-scrollbar-track {
    background: transparent !important;
    border-radius: 10px;
  }
  ::-webkit-scrollbar-thumb {
    width: 5px;
    background: transparent !important;
    border-radius: 10px;
  }
`;
const TextFieldGrid = styled.div`
  display: flex;

  align-items: center;
  gap: 20px;
  width: 100%;
  padding-bottom: 20px;
  .para-three {
    font-size: 11px;
    padding-right: 12px;
    color: #9597a6;
    flex: 0 0 calc(33% - 20px);
  }
  label {
    display: none !important;
  }
`;
const StyledNameField = styled(TextField)`
  flex: 0 0 calc(33% - 20px);
  display: flex;
  align-items: center;
  margin: 0px;
  input {
    width: 100%;
    height: 30px;
  }
`;
const StyledIdField = styled(TextField)`
  flex: 0 0 calc(33% - 20px);
  display: flex;
  align-items: center;
  margin: 0px;
  input {
    width: 100%;
    height: 30px;
  }
  ::placeholder {
  }
`;
const ButtonWrapper = styled.div`
  height: 70px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 0px 20px;

  &.bg-color {
    background: rgb(42 42 42) 100%;
  }
  &.bg-color-change {
    position: absolute;
    bottom: 0px;
    left: 0px;
    background: linear-gradient(
      to bottom,
      rgb(36 36 36 / 27%) 0%,
      rgb(42 42 42) 100%
    );
    border: 1px solid rgb(42 42 42);
    backdrop-filter: blur(5px);
  }
  @media (min-width: 992px) and (max-width: 1199px) {
    height: 50px;
    @media (min-width: 1200px) and (max-width: 1365px) {
      height: 55px;
    }
    @media (min-width: 1366px) and (max-width: 1448px) {
      height: 70px;
    }
  }
`;

export default IndividualPlacementFormTwo;
